import { BreadcrumbProps, Button, Card, CardBody, CodeDisplay, DataTable, FilterPanel, Icon, InlineEditableText, Skeleton, Spinner, Tooltip, useToast } from '@aidkitorg/component-library'
import { ReportFilterConfig } from '@aidkitorg/types/lib/admin-panel/reports'
import { ParentFieldEntry } from '@aidkitorg/types/lib/admin-panel/settings'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import { useSpinDelay } from 'spin-delay'
import { ReportData, ReportWithConfig, useReports } from '../../Contexts/ReportsContext'
import { useSettings } from '../../Contexts/SettingsContext'
import { useLocalizedStrings } from '../../Localization'
import { AdminPanelPage } from '../AdminPanelPage'
import { ReportsBuilderPanel } from './components/ReportsBuilderPanel'
import { ReportFilterProvider, useReportFilter } from './contexts/ReportFilterContext'
import { useReportFilterPanel } from './hooks/useReportFilterPanel'
import { convertBooleanExprToFilterGroup, convertFilterGroupToBooleanExpr } from './utils/filterConverters'
import { hasAnyFilters } from './utils/filters'

const SYNTHETIC_UID_COLUMN_NAME = "__synthetic_uid__";

const ReportsBuilderPageInner = ({ instanceId }: { instanceId: string }) => {
  const L = useLocalizedStrings();
  const { reportId } = useParams<{ reportId?: string }>()
  const isEditMode = Boolean(reportId)

  const { stagedConfig, setStagedConfig, createReport, updateReport, fetchReportById, reportsById, generateReportData, generateReportDataImmediate } = useReports()
  const { setAvailableFields, setFilterConfig } = useReportFilter()
  const { filterPanelProps, filterRef } = useReportFilterPanel(instanceId)

  const { fields, isFieldsLoading } = useSettings()
  const { toast } = useToast()
  const history = useHistory()

  const [title, setTitle] = useState(L.admin_panel.reports.my_report)
  const [description, setDescription] = useState('')

  // Use refs to capture the most recent values for save operations
  const titleRef = useRef(title)
  const descriptionRef = useRef(description)

  // Track saved state for unsaved changes detection
  const [savedState, setSavedState] = useState<{
    title: string
    description: string
    config: any
  } | null>(null)

  // Track when we're saving to prevent prompt during navigation
  const [isSaving, setIsSaving] = useState(false)

  // Keep refs in sync with state
  useEffect(() => {
    titleRef.current = title
  }, [title])

  useEffect(() => {
    descriptionRef.current = description
  }, [description])

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!savedState) return false

    return (
      title !== savedState.title ||
      description !== savedState.description ||
      JSON.stringify(stagedConfig) !== JSON.stringify(savedState.config)
    )
  }, [title, description, stagedConfig, savedState])

  // Initialize saved state for new reports
  useEffect(() => {
    if (!isEditMode && !savedState) {
      setSavedState({
        title: L.admin_panel.reports.my_report,
        description: '',
        config: null
      })
    }
  }, [isEditMode, savedState, L.admin_panel.reports.my_report])

  // Prevent navigation when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [existingReport, setExistingReport] = useState<ReportWithConfig | null>(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasDataLoadingStarted, setHasDataLoadingStarted] = useState(false)
  const [isForceRefreshing, setIsForceRefreshing] = useState(false);
  const [isTableExpanded, setIsTableExpanded] = useState(false);

  // Pagination state for report data preview
  const [previewPage, setPreviewPage] = useState(1)
  const [previewLimit, setPreviewLimit] = useState(50)
  const [previewTotal, setPreviewTotal] = useState(0)
  const [previewSorting, setPreviewSorting] = useState<SortingState>([])

  // Handle filter changes and update staged config
  const handleFilterChange = useCallback((newFilterConfig: ReportFilterConfig) => {
    // Only process filters if they're disabled or have complete conditions
    const hasComplete = hasAnyFilters(newFilterConfig);

    // Update staged config with converted filters
    if (newFilterConfig.enabled && hasComplete) {
      const booleanExpr = convertFilterGroupToBooleanExpr(newFilterConfig);
      setStagedConfig((currentConfig) => {
        if (!currentConfig) return currentConfig;
        return {
          ...currentConfig,
          filter: booleanExpr
        };
      });
    } else {
      // Filters disabled or incomplete - clear filter
      setStagedConfig((currentConfig) => {
        if (!currentConfig) return currentConfig;
        return {
          ...currentConfig,
          filter: undefined
        };
      });
    }
  }, [setStagedConfig, hasAnyFilters])

  // Fields are now auto-loaded by SettingsContext - no manual API calls needed

  // Available fields for filters (all fields that user has access to)
  const availableFieldsForFilters = useMemo(() => {
    return fields.map(field => ({
      key: field.id,
      label: field.displayName || field.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: !field.parent ? ((field as ParentFieldEntry).dataType ?? 'text') : 'text'
    }));
  }, [fields]);

  // Update context with available fields when they change
  useEffect(() => {
    setAvailableFields(availableFieldsForFilters)
  }, [availableFieldsForFilters, setAvailableFields])

  // Load existing report in edit mode
  // Track the last processed mode and reportId to prevent unnecessary resets
  const lastModeRef = useRef<{ isEditMode: boolean; reportId?: string }>({ isEditMode: false });

  useEffect(() => {
    const currentMode = { isEditMode, reportId };
    const lastMode = lastModeRef.current;

    // Only proceed if the mode or reportId actually changed
    if (currentMode.isEditMode !== lastMode.isEditMode || currentMode.reportId !== lastMode.reportId) {
      if (isEditMode && reportId) {
        const loadExistingReport = async () => {
          setIsLoadingReport(true)
          try {
            await fetchReportById(reportId)
          } catch (error) {
            toast({
              title: L.admin_panel.reports.error,
              description: L.admin_panel.reports.failed_to_load_report,
              variant: 'error'
            })
            history.push('/admin-panel/reports')
            setIsLoadingReport(false)
          }
        }
        loadExistingReport()
      } else if (!isEditMode && lastMode.isEditMode) {
        // Only reset when transitioning FROM edit mode TO create mode
        setTitle(L.admin_panel.reports.my_report)
        setDescription('')
        setStagedConfig(null)
        setExistingReport(null)
        setReportData(null)
        setPreviewPage(1)
        setPreviewTotal(0)
        setPreviewSorting([])
        // Initialize saved state for new reports to track changes from initial state
        setSavedState({
          title: L.admin_panel.reports.my_report,
          description: '',
          config: null
        })
        // Reset will be handled by filter context automatically
        setIsInitialLoad(false)
        loadedReportRef.current = null // Reset the loaded report tracking
      }

      lastModeRef.current = currentMode;
    }
  }, [isEditMode, reportId])

  // Watch for the report to be available in context after fetch
  // Use a ref to track if we've already loaded this report to prevent re-runs
  const loadedReportRef = useRef<string | null>(null);

  useEffect(() => {
    if (isEditMode && reportId && reportsById[reportId] && isLoadingReport && loadedReportRef.current !== reportId && !isFieldsLoading) {
      const report = reportsById[reportId];
      setExistingReport(report)
      setTitle(report.title)
      setDescription(report.description || '')
      setStagedConfig(report.config)

      // Initialize prevConfigRef with the loaded config
      prevConfigRef.current = report.config;

      // Set saved state for unsaved changes detection
      setSavedState({
        title: report.title,
        description: report.description || '',
        config: report.config
      })

      // Initialize filter context with saved filter config
      if (report.config.filter) {
        try {
          const convertedFilter = convertBooleanExprToFilterGroup(report.config.filter, availableFieldsForFilters);
          if (convertedFilter) {
            setFilterConfig({ enabled: true, config: convertedFilter });
          }
        } catch (error) {
          console.warn('Failed to convert saved filter config:', error);
        }
      } else {
        // Ensure filters are cleared for reports without saved filters
        setFilterConfig(undefined);
      }

      setIsLoadingReport(false)
      setIsInitialLoad(false)
      loadedReportRef.current = reportId
    }
  }, [reportsById, reportId, isEditMode, isLoadingReport, isFieldsLoading])


  // Use spin-delay to prevent flashing the user with a loading spinner too quickly
  const showPreviewLoadingSpinner = useSpinDelay(isPreviewLoading, {
    delay: 250,
    minDuration: 250
  })

  // Store previous config for client-side optimization comparison
  const prevConfigRef = useRef(stagedConfig);

  // Use ref to track force refresh state for proper cleanup
  const isForceRefreshingRef = useRef(isForceRefreshing);
  isForceRefreshingRef.current = isForceRefreshing;

  // Helper function for shallow column comparison
  const areColumnsEqual = React.useCallback((col1: any, col2: any): boolean => {
    if (!col1 || !col2) return col1 === col2;

    // For Field columns, compare kind and field
    if (col1.kind === 'Field' && col2.kind === 'Field') {
      return col1.field === col2.field;
    }

    // For other types, compare kind and id if available
    if (col1.kind === col2.kind) {
      return col1.id ? col1.id === col2.id : true;
    }

    return false;
  }, []);

  // Helper function to determine if config change only affects client-side rendering
  const isClientSideConfigChange = React.useCallback((newConfig: any, currentData: ReportData | null) => {
    if (!currentData || !reportData || !prevConfigRef.current) {
      return false;
    }

    const currentConfigColumns = prevConfigRef.current?.columns || [];
    const newConfigColumns = newConfig.columns || [];

    // Check if filter changed (requires API call)
    const filterChanged = JSON.stringify(newConfig.filter) !== JSON.stringify(prevConfigRef.current.filter);
    if (filterChanged) {
      return false;
    }

    // Check if any new columns were added (requires API call)
    const hasNewColumns = newConfigColumns.some((col: any) =>
      !currentConfigColumns.some((prevCol: any) => areColumnsEqual(col, prevCol))
    );

    // If new columns were added, we need to fetch from server
    if (hasNewColumns) {
      return false;
    }

    // Check if it's only column removal/reordering (subset of existing columns)
    // Since we already have the data, both removal and reordering can be handled client-side
    const isColumnSubsetOrReorder = newConfigColumns.every((col: any) =>
      currentConfigColumns.some((prevCol: any) => areColumnsEqual(col, prevCol))
    );

    return isColumnSubsetOrReorder;
  }, [reportData, areColumnsEqual])

  // Simple function to fetch report data
  const fetchReportData = React.useCallback(async (forceRefresh = false) => {
    if (!stagedConfig) {
      setReportData(null)
      setPreviewTotal(0)
      return
    }

    setIsPreviewLoading(true);
    setHasDataLoadingStarted(true);

    try {
      let data;

      if (forceRefresh) {
        data = await generateReportDataImmediate({
          config: stagedConfig,
          page: previewPage,
          limit: previewLimit,
          search: undefined,
          sorting: previewSorting,
          forceRefresh: true
        });
      } else {
        data = await generateReportData({
          config: stagedConfig,
          page: previewPage,
          limit: previewLimit,
          search: undefined,
          sorting: previewSorting
        });
      }

      if (data) {
        setReportData(data)
        setPreviewTotal(data.total)
      }
    } catch (error) {
      console.error('Error fetching report data:', error)
      toast({
        title: L.admin_panel.reports.error,
        description: L.admin_panel.reports.failed_to_load_report_data,
        variant: 'error'
      })
    } finally {
      // Don't clear loading state if force refresh is still active
      if (!isForceRefreshingRef.current) {
        setIsPreviewLoading(false)
      }
    }
  }, [stagedConfig, previewPage, previewLimit, previewSorting, generateReportData, generateReportDataImmediate, toast])


  // Handle config changes with client-side optimizations
  useEffect(() => {
    if (!stagedConfig) return;

    setIsInitialLoad(false)

    // Check if this is a client-side only change (column removal/reordering)
    const isClientSideOnlyChange = isClientSideConfigChange(stagedConfig, reportData);

    if (isClientSideOnlyChange) {
      // For client-side changes, just update prevConfigRef - columns will re-render automatically
      prevConfigRef.current = stagedConfig;
      return;
    }

    // Reset to page 1 when config changes that require API call
    if (previewPage !== 1) {
      setPreviewPage(1)
      return // Let the pagination effect handle the data fetch
    }

    // Update prevConfigRef and fetch data - ReportsContext will handle debouncing
    prevConfigRef.current = stagedConfig;
    fetchReportData()
  }, [stagedConfig, previewPage])

  // Handle pagination and sorting changes - fetch data when page, limit, or sorting changes
  useEffect(() => {
    if (stagedConfig && !isInitialLoad) {
      fetchReportData()
    }
  }, [previewPage, previewLimit, previewSorting, isInitialLoad])

  const handleForceRefresh = async () => {
    setIsForceRefreshing(true);

    await Promise.all([
      // Use immediate call to force update the data
      fetchReportData(true),
      // Minimum 400ms delay. This is a simple way to just ensure we 
      // show the loading state for at least 400ms for UX 
      new Promise(resolve => setTimeout(resolve, 400))
    ]);

    setIsForceRefreshing(false);
    setIsPreviewLoading(false);
  }

  const handleSaveReport = async () => {
    if (!stagedConfig) {
      toast({
        title: L.admin_panel.reports.error,
        description: L.admin_panel.reports.please_configure_report,
        variant: 'error'
      })
      return
    }

    setIsSaving(true)

    if (isEditMode && reportId) {
      // Update existing report
      try {
        await updateReport({ id: reportId, title: titleRef.current, description: descriptionRef.current, config: stagedConfig })
        // Update saved state after successful save
        setSavedState({
          title: titleRef.current,
          description: descriptionRef.current,
          config: stagedConfig
        })

        toast({
          title: L.admin_panel.reports.success,
          description: L.admin_panel.reports.report_updated,
          variant: 'success'
        })
        history.push("/admin-panel/reports/" + reportId)
      } catch (error) {
        toast({
          title: L.admin_panel.reports.error,
          description: `${L.admin_panel.reports.error_updating}: ${error || L.admin_panel.reports.unknown_error}`,
          variant: 'error'
        })
        setIsSaving(false) // Reset on error
      }
    } else {
      // Create new report
      const createdReport = await createReport({ title: titleRef.current, description: descriptionRef.current, config: stagedConfig })
      if (createdReport) {
        // Update saved state after successful creation
        setSavedState({
          title: titleRef.current,
          description: descriptionRef.current,
          config: stagedConfig
        })

        toast({
          title: L.admin_panel.reports.success,
          description: L.admin_panel.reports.report_created,
          variant: 'success'
        })

        history.push("/admin-panel/reports/" + createdReport)
      } else {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.error_creating,
          variant: 'error'
        })
        setIsSaving(false) // Reset on error
      }
    }
  }

  const pageActionButtons = [
    <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
      <Button
        size="sm"
        variant='secondary'
        isDisabled={!stagedConfig || isForceRefreshing}
        onClick={handleForceRefresh}
        icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
        aria-label={L.admin_panel.payments.refresh_data}
      />
    </Tooltip>,
    <Button
      variant="primary"
      onClick={handleSaveReport}
      disabled={!stagedConfig || isLoadingReport}
    >
      {isEditMode ? L.admin_panel.reports.update_report : L.admin_panel.reports.create_report}
    </Button>
  ]

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.reports,
      href: '/admin-panel/reports'
    },
    ...(existingReport ? [
      {
        label: existingReport.title,
        href: `/admin-panel/reports/${reportId}`
      }
    ] : []),
    {
      label: isEditMode ? L.admin_panel.reports.edit_report : L.admin_panel.reports.report_builder,
    }
  ]

  const titleEditor = (
    <InlineEditableText
      as="h1"
      required
      label={L.admin_panel.reports.report_title}
      value={title}
      onSave={(value) => setTitle(String(value))}
      multiline
      className="max-w-3xl"
    />
  )

  const descriptionEditor = (
    <InlineEditableText
      label={L.admin_panel.reports.report_description}
      value={description}
      placeholder={L.admin_panel.reports.add_a_description}
      onSave={(value) => setDescription(String(value))}
      multiline
      className="max-w-lg"
    />
  )

  // Memoize optimistic columns separately to avoid re-computation
  const optimisticColumns = React.useMemo(() => {
    if (!stagedConfig) return [];

    const configColumns = stagedConfig.columns || [];
    return configColumns.map(configCol => {
      const fieldName = configCol.kind === 'Field' ? configCol.field : configCol.kind;
      const displayKey = fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : fieldName;
      const displayLabel = fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'Applicant UID' : fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      return { key: displayKey, label: displayLabel };
    });
  }, [stagedConfig?.columns]);

  // Memoize available columns map to avoid recreation
  const availableColumnsMap = React.useMemo(() => {
    if (!reportData?.columns) return new Map();
    return new Map(reportData.columns.map(col => [col.key, col]));
  }, [reportData?.columns]);

  // Generate dynamic columns from report data with optimistic updates
  const columns = React.useMemo((): ColumnDef<any>[] => {
    if (!stagedConfig) return []

    const configColumns = stagedConfig.columns || [];

    // If we don't have data yet, show optimistic columns immediately
    if (!reportData || reportData.columns.length === 0) {
      return optimisticColumns.map(col => ({
        accessorKey: col.key,
        header: col.label,
        enableSorting: col.key !== 'uid',
        sortingFn: 'auto',
        sortDescFirst: false,
        cell: () => (
          <div className="flex items-center space-x-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
          </div>
        )
      }));
    }

    // Build columns in the order specified by stagedConfig
    const orderedColumns = configColumns.map(configCol => {
      const fieldName = configCol.kind === 'Field' ? configCol.field : configCol.kind;
      const actualFieldName = fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : fieldName;
      const existingColumn = availableColumnsMap.get(actualFieldName);

      if (existingColumn) {
        return existingColumn;
      } else {
        const displayLabel = fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'Applicant UID' : fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return {
          key: actualFieldName,
          label: displayLabel,
          isOptimistic: true
        };
      }
    });

    return orderedColumns.map(col => ({
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.key !== 'uid',
      sortingFn: 'auto',
      sortDescFirst: false,
      cell: ({ getValue }: any) => {
        if ('isOptimistic' in col && col.isOptimistic) {
          return (
            <div className="flex items-center space-x-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
            </div>
          );
        }

        const value = getValue()
        if (value === null || value === undefined) {
          return <span className="inline-block min-h-[14px]">--</span>
        }

        // Use CodeDisplay for UID columns
        if (col.key === 'uid') {
          return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />
        }

        return <span className="inline-block min-h-[14px]">
          {String(value)}
        </span>
      }
    }))
  }, [stagedConfig?.columns, reportData?.columns, optimisticColumns, availableColumnsMap])

  // Generate column order for drag and drop
  const columnOrder = React.useMemo(() => {
    if (!stagedConfig?.columns) return []
    return stagedConfig.columns.map(configCol => {
      const fieldName = configCol.kind === 'Field' ? configCol.field : configCol.kind
      return fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : fieldName
    })
  }, [stagedConfig?.columns])

  // Handle column reordering
  const handleColumnOrderChange = React.useCallback((newColumnOrder: string[]) => {
    if (!stagedConfig?.columns) return

    // Create a map of display names to column configs for fast lookup
    const columnConfigMap = new Map()
    stagedConfig.columns.forEach(configCol => {
      if (configCol.kind === 'Field') {
        // Handle Field columns - map synthetic UID to 'uid', others keep their field name
        const displayFieldName = configCol.field === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : configCol.field
        columnConfigMap.set(displayFieldName, configCol)
      }
      // Skip non-Field columns for now as they shouldn't be in the reordering logic
    })

    // Reorder the columns array based on the new order
    const reorderedColumns = newColumnOrder
      .map(fieldName => columnConfigMap.get(fieldName))
      .filter(Boolean) // Remove any undefined entries

    // Update the staged config with reordered columns
    setStagedConfig(prev => {
      if (!prev) return prev
      return {
        ...prev,
        columns: reorderedColumns
      }
    })
  }, [stagedConfig?.columns, setStagedConfig])

  // Handle pagination changes for preview data
  const onPreviewPaginationChange = React.useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    const newPage = pagination.pageIndex + 1; // Convert 0-based to 1-based
    const newLimit = pagination.pageSize;

    // If page size changed, reset to page 1
    if (newLimit !== previewLimit) {
      setPreviewPage(1);
      setPreviewLimit(newLimit);
    } else if (newPage !== previewPage) {
      // Only page changed, keep the same limit
      setPreviewPage(newPage);
    }
  }, [previewPage, previewLimit])

  // Handle sorting changes for preview data
  const onPreviewSortingChange = React.useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(previewSorting) : updaterOrValue;
    setPreviewSorting(newSorting);
  }, [previewSorting]);

  // Reusable expand/collapse button component
  const ExpandCollapseButton = React.useCallback(() => (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => {
        setIsTableExpanded(prevState => !prevState);
      }}
      icon={<Icon
        role="decorative"
        type="solid"
        name={isTableExpanded ? "ArrowsPointingInIcon" : "ArrowsPointingOutIcon"}
      />}
      title={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
      aria-label={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
    />
  ), [isTableExpanded, L.admin_panel.reports.collapse_table_view, L.admin_panel.reports.expand_table_view]);

  const PreviewArea = React.useMemo(() => {
    // Show initial loading state when page first loads (before any config or data)
    if (isInitialLoad) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <Spinner />
            <div className="text-lg font-medium mb-2">{L.admin_panel.reports.loading_report_builder}</div>
            <div>{L.admin_panel.reports.loading_program_details}</div>
          </div>
        </div>
      )
    }

    // Check if we have active filters but no columns selected
    const hasActiveFilters = stagedConfig?.filter !== undefined;
    const hasColumns = stagedConfig?.columns && stagedConfig.columns.length > 0;

    // Show configuration prompt when no config is set
    if (!stagedConfig) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-lg font-medium mb-2">{L.admin_panel.reports.build_report}</div>
            <div>{L.admin_panel.reports.select_fields}</div>
          </div>
        </div>
      )
    }

    // Show empty state with filter panel when filters are active but no columns selected
    if (hasActiveFilters && !hasColumns) {
      return (
        <Card className="rounded-md">
          <CardBody>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <FilterPanel
                    {...filterPanelProps}
                    onChange={handleFilterChange}
                    isReloading={isPreviewLoading}
                  />
                </div>
                <div className="hidden 2xl:inline-block">
                  <ExpandCollapseButton />
                </div>
              </div>

              <div className="flex items-center justify-center py-24 text-gray-700">
                <div className="text-center max-w-md flex flex-col gap-2 items-center">
                  <Icon name="CubeIcon" type="solid" size="xl" />
                  <div>
                    <span className="block text-lg font-medium mb-2">{L.admin_panel.reports.add_columns_to_view_data}</span>
                    <span className="block text-sm text-gray-600">
                      {L.admin_panel.reports.filters_applied_select_columns}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )
    }

    // Show error state if no data after loading (but only if we have no config - otherwise we should be loading)
    if (!reportData && !stagedConfig && hasDataLoadingStarted) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>{L.admin_panel.reports.no_data_available}</div>
            <div className="text-sm mt-1">{L.admin_panel.reports.adjust_fields}</div>
          </div>
        </div>
      )
    }

    // Show empty state when filters/columns are configured but result in no data
    if (reportData && reportData.data && reportData.data.length === 0 && hasColumns && !isPreviewLoading) {
      return (
        <Card className="rounded-md">
          <CardBody>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <FilterPanel
                    {...filterPanelProps}
                    onChange={handleFilterChange}
                    isReloading={isPreviewLoading}
                  />
                </div>
                <div className="hidden 2xl:inline-block">
                  <ExpandCollapseButton />
                </div>
              </div>

              <div className="flex items-center justify-center py-24 text-gray-700">
                <div className="text-center max-w-md flex flex-col gap-2 items-center">
                  <Icon name="MagnifyingGlassIcon" type="solid" size="xl" />
                  <div>
                    <span className="block text-lg font-medium mb-2">{L.admin_panel.reports.no_results_found}</span>
                    <span className="block text-sm text-gray-600">
                      {L.admin_panel.reports.try_adjusting_filters}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )
    }

    // Show data table with overlay when updating
    return (
      <Card className="rounded-md">
        <CardBody>
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <FilterPanel
                  {...filterPanelProps}
                  onChange={handleFilterChange}
                  isReloading={isPreviewLoading}
                />
              </div>
              <div className="hidden 2xl:inline-block">
                <ExpandCollapseButton />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={reportData?.data ?? (stagedConfig && !reportData ?
                // Show skeleton rows when we have config but no data yet
                Array.from({ length: Math.min(previewLimit, 10) }, (_, i) => ({
                  optimistic_row_id: `skeleton-${i}`
                })) :
                []
              )}
              showPagination={true}
              resizableColumns
              enableColumnReordering={true}
              initialColumnOrder={columnOrder}
              onColumnOrderChange={handleColumnOrderChange}
              loading={(() => {
                // Check if we're showing optimistic columns (columns that aren't in current data yet)
                const hasOptimisticColumns = stagedConfig && reportData &&
                  stagedConfig.columns.some(configCol => {
                    const fieldName = configCol.kind === 'Field' ? configCol.field : configCol.kind;
                    const actualFieldName = fieldName === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : fieldName;
                    return !reportData.columns.some(dataCol => dataCol.key === actualFieldName);
                  });

                const showingSkeletons = hasOptimisticColumns && isPreviewLoading;
                const shouldShowLoading = (showPreviewLoadingSpinner || isForceRefreshing) && !showingSkeletons;

                return shouldShowLoading;
              })()}
              loadingMessage={L.component_library.data_table.loading}
              pageSize={previewLimit}
              getRowId={(row: any, index: number) => row.uid || row.optimistic_row_id || `row-${index}-${JSON.stringify(row).slice(0, 50)}`}
              manualPagination={{
                pageCount: previewTotal > 0 ? Math.ceil(previewTotal / previewLimit) : 0,
                pageIndex: Math.max(0, previewPage - 1), // Convert 1-based to 0-based, ensure non-negative
                totalRows: previewTotal || 0,
                onPaginationChange: onPreviewPaginationChange
              }}
              manualSorting={{
                sorting: previewSorting,
                onSortingChange: onPreviewSortingChange
              }}
            />
          </div>
        </CardBody>
      </Card>
    )
  }, [isInitialLoad, stagedConfig, isPreviewLoading, showPreviewLoadingSpinner, reportData, columns, previewTotal, previewLimit, previewPage, previewSorting, onPreviewPaginationChange, onPreviewSortingChange, L, hasDataLoadingStarted, isTableExpanded])

  // Show loading state while loading existing report
  if (isEditMode && isLoadingReport) {
    return (
      <AdminPanelPage
        title={<div className="w-96 h-14"><Skeleton size="auto" /></div>}
        description={<div className="w-64 h-9"><Skeleton size="auto" /></div>}
        breadcrumbs={[
          {
            label: L.admin_panel.nav.reports,
            href: '/admin-panel/reports'
          }
        ]}
        fullWidth
      >
        <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 flex-1 min-h-0 overflow-auto">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex flex-col gap-y-8 items-center justify-center h-96 text-gray-500">
              <Spinner className="p-0" />
              <div>{L.admin_panel.reports.loading_report_editing}</div>
            </div>
          </div>
          <div className="flex-shrink-0 sm:w-60 lg:w-80 sm:min-h-full sm:max-h-[calc(100vh-16rem)] border rounded-md bg-white">
            <ReportsBuilderPanel isEditMode={isEditMode} />
          </div>
        </div>
      </AdminPanelPage>
    )
  }

  return (
    <AdminPanelPage
      title={titleEditor}
      description={descriptionEditor}
      pageActionButtons={pageActionButtons}
      breadcrumbs={breadcrumbs}
      fullWidth={isTableExpanded}
      keyboardShortcuts={[
        { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
        { key: 'R', description: L.admin_panel.reports.refresh_report, modifier: 'meta', callback: () => { handleForceRefresh() } },
        { key: 'S', description: L.admin_panel.reports.save_report, modifier: 'meta', callback: () => { handleSaveReport() } },
        { key: 'Z', description: L.admin_panel.reports.undo_filter_change, modifier: 'meta', },
        { key: 'Y', description: L.admin_panel.reports.redo_filter_change, modifier: 'meta' },
        { key: 'F', description: L.admin_panel.reports.toggle_filters_panel, modifier: ['meta', 'shift'], callback: () => { filterRef.current?.toggleOpen() } },
        {
          key: ['ArrowLeft', 'ArrowRight'],
          modifier: 'alt',
          description: L.admin_panel.reports.navigate_table_pages,
          callback: (key) => {
            if (key === 'ArrowLeft' && reportData && previewPage > 1) {
              setPreviewPage(previewPage - 1);
            }
            if (key === 'ArrowRight' && reportData && previewTotal > 0) {
              const totalPages = Math.ceil(previewTotal / previewLimit);
              if (previewPage < totalPages) {
                setPreviewPage(previewPage + 1);
              }
            }
          }
        },
      ]}
    >
      <Prompt
        when={hasUnsavedChanges && !isSaving}
        message={isEditMode ? L.admin_panel.reports.unsaved_changes_warning : L.admin_panel.reports.unsaved_report_warning}
      />
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 flex-1 min-h-0 overflow-auto">
        <div className="flex-1 min-w-0 overflow-hidden">
          {PreviewArea}
        </div>
        <div className="flex-shrink-0 sm:w-60 lg:w-80 sm:min-h-full sm:max-h-[calc(100vh-16rem)] border rounded-md bg-white">
          <ReportsBuilderPanel isEditMode={isEditMode} />
        </div>
      </div>
    </AdminPanelPage>
  )
}

export const ReportsBuilderPage = () => {
  const { reportId } = useParams<{ reportId?: string }>()

  // Define instance ID in one place
  const instanceId = `report-builder-${reportId || 'new'}`;

  // Clear session storage for filter panel on every page load
  useEffect(() => {
    const sessionKey = `reports-filter-${instanceId}`;
    sessionStorage.removeItem(sessionKey);
  }, [instanceId]);

  return (
    <ReportFilterProvider
      enableUrlFilters={false}
    >
      <ReportsBuilderPageInner instanceId={instanceId} />
    </ReportFilterProvider>
  )
}